import React, {useMemo} from 'react';

import {trackRunVisibilityMenu} from '../../../services/analytics/runSelectAllVisibilityMenuEvents';
import type {Ref} from '../../../state/views/filter/types';
import type {DefaultToggleFilter} from '../../../util/filterTypes';
import {useRampFlagWorkspaceFilterVisibilityUI} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type Config as TableSettings} from '../../../util/runfeed';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {WBTableActionFilter} from '../WBTable/WBTableActionsFilter';
import {RunsFilterButton} from './RunsFilterButton';
import {RunsFilterKeySelector} from './RunsFilterKeySelector';
import {RunsFilterPicker} from './RunsFilterPicker';
import {RunsFilterValueSelector} from './RunsFilterValueSelector';

interface RunsFilterTableActionProps {
  entityName: string;
  projectName: string;
  tableSettings: TableSettings;
  compact?: boolean;
  filtersRef: Ref;
  notificationOpen: boolean;
  pickerOpen: boolean;

  defaultToggleFilters?: DefaultToggleFilter[];
  setOnlyShowSelected?: (newSetting: boolean) => void;
  setPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFiltersChanged(): void;
}

export const RunsFilterTableAction = ({
  compact,
  defaultToggleFilters,
  entityName,
  filtersRef,
  notificationOpen,
  onFiltersChanged,
  pickerOpen,
  projectName,
  setOnlyShowSelected,
  setPickerOpen,
  tableSettings,
}: RunsFilterTableActionProps) => {
  const isFilterVisibilityUIEnabled = useRampFlagWorkspaceFilterVisibilityUI();
  const isEnabled = useRunSelectorFeatureEnabled();
  const isCompact = isFilterVisibilityUIEnabled ? false : compact;
  const analyticsLocation = useRunSelectorAnalyticsLocation();

  const onlyShowSelected = Boolean(tableSettings.onlyShowSelected);
  const hideManuallyHidden = useMemo(
    () =>
      isEnabled('toggle-visibility') &&
      isFilterVisibilityUIEnabled &&
      setOnlyShowSelected
        ? {
            checked: onlyShowSelected,
            onChange: (checked: boolean) => {
              setOnlyShowSelected(checked);
              // event name kept as 'Only show visualized toggled' despite the
              // renamed UI label ('List only visible runs') for analytic continuity
              trackRunVisibilityMenu({
                action: 'Only show visualized toggled',
                method: 'filter_modal',
                location: analyticsLocation,
              });
            },
          }
        : undefined,
    [
      isFilterVisibilityUIEnabled,
      onlyShowSelected,
      setOnlyShowSelected,
      analyticsLocation,
      isEnabled,
    ]
  );

  const extraFilterCount =
    isFilterVisibilityUIEnabled && onlyShowSelected ? 1 : 0;

  return (
    <WBTableActionFilter
      open={pickerOpen}
      setOpen={setPickerOpen}
      trigger={filtersOpen => (
        <RunsFilterButton
          notificationOpen={notificationOpen}
          compact={isCompact}
          filtersOpen={filtersOpen}
          filtersRef={filtersRef}
          extraFilterCount={extraFilterCount}
        />
      )}
      content={
        <RunsFilterPicker
          entityName={entityName}
          projectName={projectName}
          filtersRef={filtersRef}
          defaultToggleFilters={defaultToggleFilters}
          hideManuallyHidden={hideManuallyHidden}
          onAgentChatOpened={() => setPickerOpen(false)}
          onFiltersChanged={onFiltersChanged}
          filterKeySelector={filterKeySelectorProps => (
            <RunsFilterKeySelector
              {...filterKeySelectorProps}
              entityName={entityName}
              projectName={projectName}
              tableSettings={tableSettings}
            />
          )}
          filterValueSelector={filterValueSelectorProps => {
            return (
              <RunsFilterValueSelector
                {...filterValueSelectorProps}
                entityName={entityName}
                projectName={projectName}
              />
            );
          }}
        />
      }
    />
  );
};
